// src/hooks/useEnrollments.js
import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../supabaseClient';

/**
 * useEnrollments — React Query v5 friendly, backward-compatible with your old API.
 *
 * Exposes:
 *  - enrollments, loading, error, refetch             // list of all non-deleted enrollments
 *  - addEnrollment(row)                               // single insert (returns { success, data | error })
 *  - updateEnrollment(id, updates)                    // update one (returns { success, data | error })
 *  - deleteEnrollment(id)                             // soft delete (returns { success | error })
 *  - addEnrollmentsBulk (TanStack mutation object)    // .mutateAsync({ studentId, rows })
 *
 * Cache keys used elsewhere:
 *  - ['enrollments', studentId]  // per-student enrollments (used by StudentDetails)
 *  - ['enrollments_list']        // global list here
 */

export const useEnrollments = () => {
  const queryClient = useQueryClient();

  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* ----------------------------- Fetch (list) ----------------------------- */
  const fetchEnrollments = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('enrollments')
        .select(`
          *,
          student:students (
            id,
            full_name,
            email,
            nric_passport
          ),
          subject:subjects (
            id,
            code,
            name
          ),
          intake:intakes (
            id,
            name,
            start_date,
            end_date
          )
        `)
        .eq('is_deleted', false)
        .order('enrollment_date', { ascending: false });

      if (error) throw error;
      setEnrollments(data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching enrollments:', err);
      setError(err.message || 'Failed to fetch enrollments');
      setEnrollments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnrollments();
  }, []);

  /* --------------------------- Cache helpers ----------------------------- */
  const invalidateStudentAndList = (studentId) => {
    if (studentId) {
      queryClient.invalidateQueries({ queryKey: ['enrollments', String(studentId)] });
    }
    queryClient.invalidateQueries({ queryKey: ['enrollments_list'] });
  };

  /* ----------------------------- CREATE (single) ----------------------------- */
  const addEnrollment = async (enrollmentData) => {
    try {
      const { data, error } = await supabase
        .from('enrollments')
        .insert([
          {
            ...enrollmentData,
            is_deleted: false, // ensure soft-delete flag is false
          },
        ])
        .select(`
          *,
          student:students (
            id,
            full_name,
            email,
            nric_passport
          ),
          subject:subjects (
            id,
            code,
            name
          ),
          intake:intakes (
            id,
            name,
            start_date,
            end_date
          )
        `);

      if (error) throw error;

      const row = data?.[0] || null;
      if (row) {
        // update local list (backward-compat with your old stateful usage)
        setEnrollments((prev) => [...prev, row]);
        // invalidate per-student cache for instant refresh on StudentDetails
        invalidateStudentAndList(row.student_id);
      }
      return { success: true, data: row };
    } catch (err) {
      console.error('Error adding enrollment:', err);
      setError(err.message || 'Failed to add enrollment');
      return { success: false, error: err.message || 'Failed to add enrollment' };
    }
  };

  /* ----------------------------- CREATE (bulk) ----------------------------- */
  // TanStack mutation used by EnrollmentNew.jsx for the optimistic, instant flow.
  const addEnrollmentsBulk = useMutation({
    mutationFn: async ({ rows }) => {
      if (!Array.isArray(rows) || rows.length === 0) return [];
      const { data, error } = await supabase
        .from('enrollments')
        .insert(rows.map((r) => ({ ...r, is_deleted: false })))
        .select(`
          *,
          student:students (
            id,
            full_name,
            email,
            nric_passport
          ),
          subject:subjects (
            id,
            code,
            name
          ),
          intake:intakes (
            id,
            name,
            start_date,
            end_date
          )
        `);
      if (error) throw error;
      return data || [];
    },
    onSuccess: (data, variables) => {
      // keep local list roughly in sync for pages that still consume this hook directly
      if (Array.isArray(data) && data.length) {
        setEnrollments((prev) => [...prev, ...data]);
      }
      const studentId = variables?.studentId ?? data?.[0]?.student_id;
      invalidateStudentAndList(studentId);
    },
    onError: (err) => {
      console.error('Bulk enrollment insert failed:', err);
      setError(err.message || 'Bulk insert failed');
    },
  });

  /* ------------------------------- UPDATE -------------------------------- */
  const updateEnrollment = async (id, updates) => {
    try {
      const { data, error } = await supabase
        .from('enrollments')
        .update(updates)
        .eq('id', id)
        .select(`
          *,
          student:students (
            id,
            full_name,
            email,
            nric_passport
          ),
          subject:subjects (
            id,
            code,
            name
          ),
          intake:intakes (
            id,
            name,
            start_date,
            end_date
          )
        `);

      if (error) throw error;

      const row = data?.[0] || null;
      if (row) {
        setEnrollments((prev) => prev.map((enr) => (enr.id === id ? row : enr)));
        invalidateStudentAndList(row.student_id);
      }
      return { success: true, data: row };
    } catch (err) {
      console.error('Error updating enrollment:', err);
      setError(err.message || 'Failed to update enrollment');
      return { success: false, error: err.message || 'Failed to update enrollment' };
    }
  };

  /* ------------------------------- DELETE (soft) ------------------------------- */
  const deleteEnrollment = async (id) => {
    try {
      // We need the student_id to invalidate the correct cache key
      const currentRow = enrollments.find((e) => e.id === id);
      const studentId = currentRow?.student_id;

      const { error } = await supabase
        .from('enrollments')
        .update({ is_deleted: true })
        .eq('id', id);

      if (error) throw error;

      setEnrollments((prev) => prev.filter((enr) => enr.id !== id));
      invalidateStudentAndList(studentId);
      return { success: true };
    } catch (err) {
      console.error('Error deleting enrollment:', err);
      setError(err.message || 'Failed to delete enrollment');
      return { success: false, error: err.message || 'Failed to delete enrollment' };
    }
  };

  /* ---------------------- BULK UPDATE STATUS (RPC) ---------------------- */
  // Usage: bulkUpdateEnrollmentStatus.mutateAsync({
  //   studentId,
  //   intakeId,
  //   enrollmentIds, // array of UUID strings
  //   targetStatus,  // 'Completed' or 'Withdrawn'
  // })
  const bulkUpdateEnrollmentStatus = useMutation({
    mutationFn: async ({ studentId, intakeId, enrollmentIds, targetStatus }) => {
      if (!studentId || !intakeId || !Array.isArray(enrollmentIds)) {
        throw new Error('Missing parameters for bulk_update_enrollment_status');
      }

      const { data, error } = await supabase.rpc('bulk_update_enrollment_status', {
        p_student_id: studentId,
        p_intake_id: intakeId,
        p_enrollment_ids: enrollmentIds,
        p_target_status: targetStatus,
      });

      if (error) {
        console.error('RPC bulk_update_enrollment_status error:', error);
        throw new Error(error.message || 'Bulk update failed');
      }

      return data; // { updated_count, skipped_count, results: [...] }
    },
    onSuccess: (data, variables) => {
      const studentId = variables?.studentId;
      invalidateStudentAndList(studentId);
      // You can also log data here for debugging:
      // console.log('Bulk update result:', data);
    },
    onError: (err) => {
      console.error('Bulk update enrollment status failed:', err);
      setError(err.message || 'Bulk update failed');
    },
  });

  return {
    // list state (backward compatible with your original hook)
    enrollments,
    loading,
    error,
    refetch: fetchEnrollments,

    // single create/update/delete helpers (compatible with existing calls)
    addEnrollment,
    updateEnrollment,
    deleteEnrollment,

    // new: bulk mutation for instant UX (use .mutateAsync({ studentId, rows }))
    addEnrollmentsBulk,


    // new: bulk status update via RPC
    bulkUpdateEnrollmentStatus,

  };
};
